<script setup lang="ts">
  import { type EnumValues } from '@travelata-libs/utils';
  import { ref, computed } from 'vue';
  import { Button, Typo, Floating, FloatingTrigger, FloatingPlacement } from '@/components';

  interface PopoverProps {
    width?: string;
    trigger?: EnumValues<typeof FloatingTrigger>;
    placement?: EnumValues<typeof FloatingPlacement>;
    title?: string;
    closable?: boolean;
    visible?: boolean;
    zIndex?: number;
    disabled?: boolean;
  }

  interface PopoverEmits {
    (e: 'change-visible', value: boolean): void;
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<PopoverProps>(), {
    width: 'auto',
    title: '',
    visible: false,
    closable: false,
    trigger: FloatingTrigger.Click,
    placement: FloatingPlacement.Bottom,
    zIndex: 5,
    disabled: false,
  });

  const emit = defineEmits<PopoverEmits>();

  const floatingElement = ref(null);

  const contentStyle = computed(() => {
    return {
      width: props.width,
      zIndex: props.zIndex,
    };
  });

  const close = () => {
    floatingElement.value?.hide();
  };

  const open = () => {
    floatingElement.value?.open();
  };

  defineExpose({
    close,
    open,
  });
</script>

<template>
  <Floating
    ref="floatingElement"
    class="tl-popover"
    :placement="placement"
    :visible="visible"
    :trigger="trigger"
    :offset="12"
    :disabled="disabled"
    @change-visible="emit('change-visible', $event)"
    @hide="emit('close')"
  >
    <template #reference>
      <slot name="reference" />
    </template>
    <template #floating>
      <div data-test-id="floating" class="tl-popover__floating" :style="contentStyle">
        <header v-if="$slots.header || title || closable" class="tl-popover__header">
          <div>
            <slot name="header">
              <Typo v-if="title" variant="body-l-500">
                {{ title }}
              </Typo>
            </slot>
          </div>
          <Button
            v-if="closable"
            data-test-id="close-btn"
            type="icon"
            icon="close"
            appearance="tertiary"
            size="m"
            @click="close"
          />
        </header>

        <div v-if="$slots.default" class="tl-popover__content">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="tl-popover__footer">
          <slot name="footer" />
        </footer>
      </div>
    </template>
  </Floating>
</template>

<style lang="scss" scoped>
  .tl-popover {
    &__floating {
      background: var(--color-bg-primary);
      border-radius: var(--rounding-s);
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      color: var(--color-text-primary);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      max-width: 100vw;
      padding: var(--spacing-m);
      position: relative;
    }

    &__header {
      align-items: flex-start;
      display: flex;
      gap: var(--spacing-s);
      justify-content: space-between;
    }

    &__content {
      @include typography-body(m, 400);
    }

    &__footer {
      align-items: center;
      display: flex;
      gap: var(--spacing-m);
    }
  }
</style>
